import { useState, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/da2bfldv7gurs51efav0';

export default function ContactForm() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Check honeypot
    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      if (formRef.current) {
        formRef.current.reset();
      }
      return;
    }

    // Remove honeypot from payload
    formData.delete('company_alt');

    // Validate
    const email = formData.get('email');
    if (!email || !String(email).includes('@')) {
      setStatus('error');
      setErrorMsg('Por favor, informe um e-mail válido.');
      return;
    }

    const phone = formData.get('phone');
    if (!phone || String(phone).trim().length < 8) {
      setStatus('error');
      setErrorMsg('Por favor, informe um telefone válido.');
      return;
    }

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // Not JSON
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        responseText;

      const isSpam =
        serverMsg.toLowerCase().includes('spam') ||
        serverMsg.toLowerCase().includes('form data is spam');

      if (!response.ok || isSpam || parsed?.code !== 'OK') {
        setStatus('error');
        setErrorMsg(serverMsg || 'Ocorreu um erro ao enviar. Por favor, tente novamente.');
        return;
      }

      setStatus('success');
      if (formRef.current) {
        formRef.current.reset();
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg('Erro de conexão. Verifique sua internet e tente novamente.');
    }
  };

  return (
    <section id="contato" className="py-16 md:py-20 lg:py-24 bg-background-100">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block text-accent-600 font-semibold text-sm uppercase tracking-wider mb-3">
              Contato
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4">
              {t('contact.title')}
            </h2>
            <p className="text-foreground-600 text-base md:text-lg max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 rounded-lg p-5 md:p-8 border border-background-200/70"
              >
                {/* Honeypot */}
                <div className="honeypot-field">
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('contact.form.name')} *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      placeholder="Seu nome completo"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('contact.form.email')} *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      placeholder="seu@email.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('contact.form.phone')} *
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      placeholder="(27) 99999-9999"
                    />
                  </div>

                  {/* Service type */}
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('contact.form.service')}
                    </label>
                    <select
                      id="service"
                      name="service"
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    >
                      <option value="">Selecione...</option>
                      <option value="transporte">{t('contact.form.service.option1')}</option>
                      <option value="dedicado">{t('contact.form.service.option2')}</option>
                      <option value="redespacho">{t('contact.form.service.option3')}</option>
                      <option value="outro">{t('contact.form.service.option4')}</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div className="mb-5">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
                    placeholder={t('contact.form.message.placeholder')}
                  />
                  <p className="text-xs text-foreground-500 mt-1">Máximo 500 caracteres</p>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md font-semibold text-base transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    {status === 'sending' ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      <i className="ri-send-plane-line" />
                    )}
                  </div>
                  {status === 'sending' ? t('contact.form.sending') : t('contact.form.submit')}
                </button>

                {/* Status messages */}
                {status === 'success' && (
                  <div className="mt-4 p-3 bg-accent-100/80 border border-accent-300 rounded-md flex items-start gap-2 animate-fade-in">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                      <i className="ri-check-line text-accent-700" />
                    </div>
                    <p className="text-sm text-accent-900">{t('contact.form.success')}</p>
                  </div>
                )}
                {status === 'error' && (
                  <div className="mt-4 p-3 bg-red-100/80 border border-red-300 rounded-md flex items-start gap-2 animate-fade-in">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                      <i className="ri-error-warning-line text-red-700" />
                    </div>
                    <p className="text-sm text-red-900">{errorMsg}</p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact info sidebar */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-1">
                {t('contact.info.title')}
              </h3>

              <a
                href="tel:+552737236099"
                className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300/60 transition-colors group"
                rel="nofollow"
              >
                <div className="w-10 h-10 bg-primary-100 rounded-md flex items-center justify-center flex-shrink-0">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-primary-600" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider">{t('contact.info.phone')}</p>
                  <p className="text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">(27) 3723-6099</p>
                </div>
              </a>

              <a
                href="https://wa.me/552737236099"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-accent-300/60 transition-colors group"
              >
                <div className="w-10 h-10 bg-accent-100 rounded-md flex items-center justify-center flex-shrink-0">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-accent-700" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider">{t('contact.info.whatsapp')}</p>
                  <p className="text-base font-semibold text-foreground-950 group-hover:text-accent-600 transition-colors">(27) 3723-6099</p>
                </div>
              </a>

              <a
                href="mailto:speed@speedcourier.com.br"
                className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300/60 transition-colors group"
                rel="nofollow"
              >
                <div className="w-10 h-10 bg-primary-100 rounded-md flex items-center justify-center flex-shrink-0">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-primary-600" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider">{t('contact.info.email')}</p>
                  <p className="text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors break-all">speed@speedcourier.com.br</p>
                </div>
              </a>

              <div className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-10 h-10 bg-secondary-100 rounded-md flex items-center justify-center flex-shrink-0">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-map-pin-line text-secondary-700" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider">{t('contact.info.address')}</p>
                  <p className="text-sm text-foreground-700">Rua São Pedro, Mario Giurizatto</p>
                  <p className="text-sm text-foreground-700">Colatina - ES, CEP: 29706-564</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-10 h-10 bg-secondary-100 rounded-md flex items-center justify-center flex-shrink-0">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-time-line text-secondary-700" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider">{t('contact.info.hours')}</p>
                  <p className="text-sm text-foreground-700">{t('contact.info.hours.value')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}